import {
  calculateAngledAxisBandSize,
  calculateCategoryYAxisWidth,
  calculateVerticalTickAxisBandSize,
  CATEGORY_LABEL_WRAP_WIDTH,
  measureChartTextWidth,
  wrapCategoryLabelLines,
} from '../shared/chartAxisUtils'
import { WaterfallDataItem, WaterfallStepColors, WaterfallStepType } from './types'

export {
  calculateAngledAxisBandSize,
  calculateCategoryYAxisWidth,
  calculateVerticalTickAxisBandSize,
  CATEGORY_LABEL_WRAP_WIDTH,
  measureChartTextWidth,
  wrapCategoryLabelLines,
}

export interface TransformedWaterfallRow extends WaterfallDataItem {
  base: number
  barSize: number
  runningEnd: number
  barColor: string
  displayValue: number
  stepType: WaterfallStepType
}

function normalizeStepType(raw: string | number): WaterfallStepType {
  const step = String(raw).toLowerCase()
  if (step === 'total' || step === 'increase' || step === 'decrease') {
    return step
  }
  throw new Error(`Invalid waterfall step type: ${raw}`)
}

export function transformWaterfallData(
  data: WaterfallDataItem[],
  categoryKey: string,
  valueKey: string,
  stepTypeKey: string,
  colors: WaterfallStepColors
): TransformedWaterfallRow[] {
  let runningTotal = 0

  return data.map((item) => {
    const category = String(item[categoryKey])
    const rawValue = Number(item[valueKey])
    const stepType = normalizeStepType(item[stepTypeKey])

    if (Number.isNaN(rawValue)) {
      throw new Error(`Waterfall value is not numeric for category "${category}"`)
    }

    if (stepType === 'total') {
      runningTotal = rawValue
      return {
        ...item,
        [categoryKey]: category,
        base: 0,
        barSize: rawValue,
        runningEnd: rawValue,
        barColor: colors.total,
        displayValue: rawValue,
        stepType,
      }
    }

    if (stepType === 'increase') {
      const base = runningTotal
      runningTotal += rawValue
      return {
        ...item,
        [categoryKey]: category,
        base,
        barSize: rawValue,
        runningEnd: runningTotal,
        barColor: colors.increase,
        displayValue: rawValue,
        stepType,
      }
    }

    const delta = rawValue <= 0 ? rawValue : -Math.abs(rawValue)
    const base = runningTotal + delta
    const barSize = Math.abs(delta)
    runningTotal += delta

    return {
      ...item,
      [categoryKey]: category,
      base,
      barSize,
      runningEnd: runningTotal,
      barColor: colors.decrease,
      displayValue: delta,
      stepType,
    }
  })
}

export function computeWaterfallExtents(rows: TransformedWaterfallRow[]): { min: number; max: number } {
  if (rows.length === 0) {
    return { min: 0, max: 0 }
  }

  let min = 0
  let max = 0

  for (const row of rows) {
    min = Math.min(min, row.base, row.runningEnd)
    max = Math.max(max, row.base + row.barSize, row.runningEnd)
  }

  return { min, max }
}

export function generateDataAxisTicks(minValue: number, maxValue: number): number[] {
  if (maxValue === minValue) {
    return [minValue]
  }

  const span = maxValue - minValue
  const roughTickCount = 5
  const roughInterval = span / roughTickCount
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)))
  const normalizedInterval = roughInterval / magnitude

  let niceInterval: number
  if (normalizedInterval <= 1) {
    niceInterval = magnitude
  } else if (normalizedInterval <= 2) {
    niceInterval = 2 * magnitude
  } else if (normalizedInterval <= 5) {
    niceInterval = 5 * magnitude
  } else {
    niceInterval = 10 * magnitude
  }

  const decimalPlaces = niceInterval >= 1 ? 0 : Math.ceil(-Math.log10(niceInterval))
  const niceMin = parseFloat((Math.floor(minValue / niceInterval) * niceInterval).toFixed(decimalPlaces))
  const niceMax = parseFloat((Math.ceil(maxValue / niceInterval) * niceInterval).toFixed(decimalPlaces))
  const tickCount = Math.round((niceMax - niceMin) / niceInterval)

  const ticks: number[] = []
  for (let i = 0; i <= tickCount; i++) {
    ticks.push(parseFloat((niceMin + i * niceInterval).toFixed(decimalPlaces)))
  }

  return ticks
}

export function formatWaterfallValue(value: number, unit?: string): string {
  const formatted = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value)
  return unit ? `${formatted} ${unit}` : formatted
}
